import { MdDeleteForever, MdCloudUpload } from "react-icons/md";
import { FaFilePdf, FaCheckCircle, FaRegCircle } from "react-icons/fa";
import axiosInstance from "../../api/axiosInstance.js";
import formatDateIndian from "../../common/formatDateIndian.js";

const QuotesFiles = ({ setFiles, files, isEditing }) => {
  const handleAttachFile = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    if (selectedFile.type === "application/pdf") {
      const newFile = {
        id: Date.now(),
        seq_No: 0,
        file_Name_Path: selectedFile.name,
        file: selectedFile,
        file_Name: selectedFile.name,
        attach_Date: new Date().toISOString(),
        quote_Amount: "",
        is_Active: 0,
      };

      setFiles((prev) => {
        const updated = isEditing
          ? [...prev, newFile]
          : [...prev.map((f) => ({ ...f, is_Active: false })), newFile];

        return updated;
      });
    } else {
      alert("Only PDF files are allowed.");
    }
  };

  const handleFilesDelete = async (fileId) => {
    try {
      setFiles((prev) =>
        prev.filter((f) => {
          const isNew = f.seq_No === 0 || !f.seq_No;
          return isNew ? f.id !== fileId : f.seq_No !== fileId;
        })
      );
      if (fileId && typeof fileId === "number" && fileId > 0) {
        await axiosInstance.delete(`WindowQuotation/DeleteFile/${fileId}`);
      }
    } catch (error) {
      console.error("Error deleting file:", error);
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-xl shadow-slate-200/40 relative">
      <div className="bg-slate-50/50 px-6 py-4 border-b border-slate-100 flex justify-between items-center relative overflow-hidden">
        <div className="absolute top-0 left-0 w-1 h-full bg-primary/40"></div>
        <div className="flex flex-col">
          <h2 className="text-[11px] font-black text-slate-700 uppercase tracking-widest flex items-center gap-2">
            Attachment Repository
            <span className="bg-primary/10 text-primary text-[9px] px-2 py-0.5 rounded-full ml-1 font-bold">
              {files?.length || 0}
            </span>
          </h2>
          <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">Manage quotation documents and PDFs</p>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="file"
            accept="application/pdf"
            onChange={handleAttachFile}
            className="hidden"
            id="file-upload"
          />
          <label
            htmlFor="file-upload"
            className="cursor-pointer px-4 py-2 bg-primary text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-primary/90 transition-all flex items-center gap-2 shadow-lg shadow-primary/20 active:scale-95"
          >
            <MdCloudUpload className="text-base" />
            Attach PDF
          </label>
        </div>
      </div>

      <div className="w-full overflow-x-auto">
        <table className="w-full text-left border-separate border-spacing-0">
          <thead>
            <tr className="bg-white">
              {[
                "No",
                "Document Name",
                "Date Added",
                "Quote Amount",
                "Status",
                "Action",
              ].map((header, index) => (
                <th
                  key={index}
                  className="px-6 py-3.5 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {files?.length > 0 ? (
              files.map((file, index) => (
                <tr key={file.seq_No || file.id} className="group hover:bg-slate-50/30 transition-all text-sm">
                  <td className="px-6 py-3 font-bold text-slate-400">
                    {index + 1}
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-50 text-red-500 rounded-lg group-hover:bg-red-500 group-hover:text-white transition-all">
                        <FaFilePdf size={16} />
                      </div>
                      <a
                        href={file.file_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-bold text-slate-700 hover:text-primary transition-colors cursor-pointer truncate max-w-[200px]"
                        title={file.file_Name || file.file_Name_Path || (file.file && file.file.name)}
                      >
                        {file.file_Name ||
                          file.file_Name_Path ||
                          (file.file && file.file.name)}
                      </a>
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex flex-col">
                      <span className="font-bold text-slate-600">
                        {formatDateIndian(file.attach_Date)}
                      </span>
                    </div>
                  </td>

                  <td className="px-6 py-3">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-black text-[10px]">₹</span>
                      <input
                        type="text"
                        name="quote_Amount"
                        value={file.quote_Amount}
                        placeholder="0.00"
                        onChange={(e) =>
                          setFiles((prev) =>
                            prev.map((f) =>
                              (f.seq_No || f.id) === (file.seq_No || file.id)
                                ? { ...f, quote_Amount: e.target.value }
                                : f
                            )
                          )
                        }
                        className="bg-slate-50/50 border border-slate-100 rounded-lg pl-6 pr-3 py-1.5 w-28 font-bold text-slate-700 outline-none focus:border-primary focus:bg-white transition-all text-xs"
                      />
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <button
                      type="button"
                      onClick={() =>
                        setFiles((prevFiles) =>
                          prevFiles.map((f) => {
                            const isClicked = (f.seq_No || f.id) === (file.seq_No || file.id);
                            return {
                              ...f,
                              is_Active: isClicked ? !f.is_Active : false,
                            };
                          })
                        )
                      }
                      className="flex items-center gap-2 group/btn"
                    >
                      {file.is_Active ? (
                        <div className="flex items-center gap-2 bg-emerald-50 text-emerald-600 px-2 py-1 rounded-full border border-emerald-100">
                          <FaCheckCircle className="text-[10px]" />
                          <span className="text-[9px] font-black uppercase tracking-widest">Active</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 bg-slate-50 text-slate-400 px-2 py-1 rounded-full border border-slate-100 group-hover/btn:border-primary/30 group-hover/btn:text-primary transition-all">
                          <FaRegCircle className="text-[10px]" />
                          <span className="text-[9px] font-black uppercase tracking-widest">Set Active</span>
                        </div>
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-3">
                    <button
                      type="button"
                      onClick={() =>
                        handleFilesDelete(file.seq_No || file.id)
                      }
                      className="p-2 bg-red-50 text-red-500 rounded-xl hover:bg-red-500 hover:text-white transition-all active:scale-90"
                    >
                      <MdDeleteForever size={18} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="p-5 overflow-hidden bg-slate-50 rounded-full text-slate-300">
                      <MdCloudUpload size={40} className="animate-bounce" />
                    </div>
                    <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">No documents attached yet</p>
                    <label htmlFor="file-upload" className="text-[10px] font-black text-primary hover:underline cursor-pointer uppercase tracking-widest">Click to upload your first quote</label>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default QuotesFiles;
